import React, { useState } from "react";
import { useDispatch } from "react-redux";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { Plus, Trash2 } from "lucide-react";
import { addOrUpdateAppointmentItem } from "./appointmentAPI";
import { useIntl } from "react-intl";
import { useToast } from "../../hooks/use-toast";
import QuantityStepper from "../../components/QuantityStepper";
function EquipmentTab({
  inventory = [],
  appointmentItems = [],
  appointmentId,
}) {
  const dispatch = useDispatch();
  const intl = useIntl();
  const { toast } = useToast();

  const [selectedEquipmentId, setSelectedEquipmentId] = useState("");
  const [equipmentQuantity, setEquipmentQuantity] = useState(1);
  const [equipmentNotes, setEquipmentNotes] = useState("");
  const [adding, setAdding] = useState(false);
  const [newEquipment, setNewEquipment] = useState([]);

  const handleAddEquipment = () => {
    if (!selectedEquipmentId) return;
    const selected = inventory?.find(
      (item) => String(item.id) === String(selectedEquipmentId)
    );
    if (!selected) return;

    const payload = {
      id: 0,
      appointmentId,
      productServiceId: selected.id,
      productName: selected.name,
      description: equipmentNotes || selected.description || null,
      quantity: equipmentQuantity,
      unitPrice: selected.salePrice || 0,
      discount: 0,
      amount: equipmentQuantity * (selected.salePrice || 0),
    };

    setAdding(true);
    dispatch(addOrUpdateAppointmentItem(appointmentId, payload))
      .then((savedItem) => {
        setNewEquipment((prev) => [
          {
            id: savedItem?.id || Date.now(),
            productName: payload.productName,
            quantity: payload.quantity,
            amount: payload.amount,
            description: payload.description,
            isNew: true,
          },
          ...prev,
        ]);
        setSelectedEquipmentId("");
        setEquipmentQuantity(1);
        setEquipmentNotes("");
      })
      .catch((error) => {
        toast({
          title: intl.formatMessage({
            id: "Equipment.Error",
            defaultMessage: "Error",
          }),
          description: intl.formatMessage({
            id: `EquipmentTab.AddError: ${error}`,
            defaultMessage: `Failed to add equipment: ${error}`,
          }),
          variant: "error",
        });
      })
      .finally(() => setAdding(false));
  };

  const handleRemoveEquipment = (id) => {
    setNewEquipment((prev) => prev.filter((item) => item.id !== id));
  };

  const fullList = [
    ...newEquipment,
    ...appointmentItems.map((item) => ({
      id: item.id,
      productName: item.productName,
      quantity: item.quantity,
      amount: item.amount,
      description: item.description,
      isNew: false,
    })),
  ];

  return (
    <>
      {/* Add Equipment */}
      <Card>
        <CardHeader className="!p-4">
          <CardTitle className="flex items-center gap-2 text-base">
            {intl.formatMessage({ id: "EquipmentTab.AddTitle" })}
          </CardTitle>
        </CardHeader>
        <CardContent className="!p-4 space-y-4">
          <div>
            <Label>
              {intl.formatMessage({ id: "EquipmentTab.SelectLabel" })}
            </Label>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="w-full justify-start">
                  {selectedEquipmentId
                    ? inventory.find(
                        (item) =>
                          String(item.id) === String(selectedEquipmentId)
                      )?.name
                    : intl.formatMessage({
                        id: "EquipmentTab.SelectPlaceholder",
                      })}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-full">
                {inventory.map((item) => (
                  <DropdownMenuItem
                    key={item.id}
                    onClick={() => setSelectedEquipmentId(item.id)}
                  >
                    {item.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>
                {intl.formatMessage({ id: "EquipmentTab.Quantity" })}
              </Label>
              <QuantityStepper
                value={equipmentQuantity}
                onChange={setEquipmentQuantity}
              />
            </div>
            <div>
              <Label>
                {intl.formatMessage({ id: "EquipmentTab.DescriptionOpt" })}
              </Label>
              <Input
                value={equipmentNotes}
                onChange={(e) => setEquipmentNotes(e.target.value)}
                placeholder={intl.formatMessage({
                  id: "EquipmentTab.DescriptionPlaceholder",
                })}
              />
            </div>
          </div>
          <Button
            onClick={handleAddEquipment}
            className="w-full"
            disabled={!selectedEquipmentId || adding}
          >
            <Plus className="h-4 w-4 mr-2" />
            {adding
              ? intl.formatMessage({ id: "EquipmentTab.Adding" })
              : intl.formatMessage({ id: "EquipmentTab.AddButton" })}
          </Button>
        </CardContent>
      </Card>

      {/* List of Equipment */}
      <Card>
        <CardHeader className="!p-4 !pb-0">
          <CardTitle className="text-base">
            {intl.formatMessage({ id: "EquipmentTab.ListTitle" })}
          </CardTitle>
        </CardHeader>
        <CardContent className="!p-4">
          {fullList.length === 0 ? (
            <p className="text-center text-gray-500 py-4">
              {intl.formatMessage({ id: "EquipmentTab.NoItems" })}
            </p>
          ) : (
            <div className="space-y-3">
              {fullList.map((equipment) => (
                <div
                  key={equipment.id}
                  className="equipment-item flex items-center justify-between border-b py-2"
                >
                  <div className="flex-1">
                    <div className="font-medium">
                      {equipment.productName ||
                        intl.formatMessage({ id: "EquipmentTab.Unnamed" })}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      {intl.formatMessage(
                        { id: "EquipmentTab.QuantityValue" },
                        { value: equipment.quantity }
                      )}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      {intl.formatMessage(
                        { id: "EquipmentTab.AmountValue" },
                        { value: equipment.amount }
                      )}
                    </div>
                    {equipment.description && (
                      <div className="text-sm text-gray-500 mt-1">
                        {equipment.description}
                      </div>
                    )}
                  </div>
                  {equipment.isNew && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveEquipment(equipment.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
}

export default EquipmentTab;
